/* „Ostatnie sesje" — KANON „Karty zadań" layout A, `.a-table` with `canvas.css`'s column set
 * (24 September 2026): Data 92 · Sesja (the rest) · Serie 80 · Tonaż 120 · Czas 70 · Nastrój 80 ·
 * Sen 80 · Notatki 88. `table-layout: fixed` so the fixed columns hold and the session name takes
 * what is left, whatever the rows say.
 *
 * Text that must be read is `--text-2` where the canon writes `--text-3`: `--text-3` measures 3.0:1
 * (Flame) and 4.0:1 (Flame Dark) on these surfaces — the standing deviation `DetailTab.module.css`
 * records, kept by the 31.08 decision on a measured contrast number. */

.card {
  overflow: hidden;
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-card);
}

.table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.head th {
  height: 34px;
  padding: var(--space-0) var(--space-6px);
  font-size: var(--fs-badge);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  color: var(--text-2);
  text-align: start;
  letter-spacing: var(--ls-lab);
  text-transform: uppercase;
  white-space: nowrap;
  background: var(--bg-inset);
  border-bottom: var(--border-hairline) solid var(--border);
}

.row > td,
.row > th {
  padding: var(--space-6px);
  vertical-align: middle;
}

.head th:first-child,
.row > :first-child {
  padding-inline-start: var(--space-14px);
}

.head th:last-child,
.row > :last-child {
  padding-inline-end: var(--space-14px);
}

.colDate {
  width: 92px;
}

.colSets {
  width: 80px;
}

.colTonnage {
  width: 120px;
}

.colTime {
  width: 70px;
}

.colBar {
  width: 80px;
}

.colNotes {
  width: 88px;
}

.end {
  text-align: end;
}

/* The row is the containing block for its name button's hit area, which is stretched over it. */
.row {
  position: relative;
  height: 48px;
  border-bottom: var(--border-hairline) solid var(--border);
}

.row:last-child {
  border-bottom: 0;
}

.row:has(.open:hover) {
  background: var(--bg-inset);
}

.missed {
  background: var(--bg-inset);
}

.date {
  font-size: var(--fs-micro);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-auto);
  color: var(--text-2);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.name {
  min-width: 0;
  font-weight: inherit;
  text-align: start;
}

.nameLine {
  display: flex;
  gap: var(--space-6px);
  align-items: center;
  min-width: 0;
}

.nameText,
.open {
  overflow: hidden;
  font-size: var(--fs-exercise);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  color: var(--text);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.missed .nameText {
  color: var(--text-2);
}

.open {
  padding: var(--space-0);
  font-family: inherit;
  text-align: start;
  cursor: pointer;
  background: transparent;
  border: 0;
}

/* The whole row is the target; the button is its one tab stop and its name. */
.open::after {
  position: absolute;
  inset: var(--space-0);
  content: '';
}

.open:focus-visible {
  outline: none;
}

.row:has(.open:focus-visible) {
  outline: var(--border-focus) solid var(--accent);
  outline-offset: var(--outline-inset);
}

.star {
  display: inline-grid;
  flex: none;
  place-items: center;
  width: 17px;
  height: 17px;
  font-size: var(--fs-micro);
  line-height: var(--lh-none);
  color: var(--on-pr);
  background: var(--pr);
  border-radius: var(--radius-4px);
}

.sub {
  display: block;
  margin-block-start: var(--space-2px);
  font-size: var(--fs-caption);
  font-weight: var(--fw-medium);
  line-height: var(--lh-auto);
  color: var(--text-2);
}

.num {
  font-size: var(--fs-micro);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-auto);
  color: var(--text-2);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.sets {
  font-size: var(--fs-client-cell);
  font-weight: var(--fw-bold);
  color: var(--text);
}

.delta {
  margin-inline-start: var(--space-6px);
  font-size: var(--fs-caption);
  font-weight: var(--fw-bold);
  color: var(--text-2);
}

.up {
  color: var(--good-ink);
}

.down {
  color: var(--danger);
}

/* The WORD takes the text-grade red; `--danger` is a mark colour and read 3.24:1 as words. The
 * outline is a mark and keeps it. */
.missedChip {
  display: inline-flex;
  align-items: center;
  height: var(--h-detail-pill);
  padding: var(--space-0) var(--space-2);
  font-size: var(--fs-caption);
  font-weight: var(--fw-bold);
  line-height: var(--lh-auto);
  color: var(--danger-text-strong);
  white-space: nowrap;
  border: var(--border-hairline) solid var(--danger);
  border-radius: var(--r-pill);
}

/* A self-report as a short bar beside its figure — mood in the mood series' ink, sleep in the
 * sleep series', the owner's one threshold rule painting the ends (`wellbeingBand`). */
.bar {
  position: relative;
  display: inline-block;
  width: 22px;
  height: 4px;
  margin-inline-end: var(--space-6px);
  overflow: hidden;
  vertical-align: middle;
  background: var(--border-2);
  border-radius: var(--radius-1px);
}

.barFill {
  position: absolute;
  inset-block: var(--space-0);
  inset-inline-start: var(--space-0);
  display: block;
  background: var(--bar-ink);
  border-radius: var(--radius-1px);
}

.mood {
  --bar-ink: var(--chart-series-mood);
}

.sleep {
  --bar-ink: var(--chart-series-sleep);
}

.barLow {
  background: var(--danger);
}

.barHigh {
  background: var(--good);
}

.faces {
  display: inline-flex;
  gap: var(--space-1);
  justify-content: flex-end;
}

.face {
  display: inline-grid;
  flex: none;
  place-items: center;
  width: 24px;
  height: 24px;
  font-size: var(--fs-badge);
  font-weight: var(--fw-extrabold);
  line-height: var(--lh-none);
  border-radius: var(--r-circle);
}

.faceYou {
  color: var(--bg-card);
  background: var(--text);
}

.faceClient {
  color: var(--text);
  background: var(--bg-chip);
  border: var(--border-hairline) solid var(--border-3);
}

.faceNone {
  border: var(--border-hairline) dashed var(--border-3);
}

.foot {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-6px) var(--space-4);
  align-items: center;
  margin: var(--space-0);
  padding: var(--space-10px) var(--space-14px);
  font-size: var(--fs-caption);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-auto);
  color: var(--text-2);
  border-top: var(--border-hairline) solid var(--border);
}

.legend {
  display: inline-flex;
  gap: var(--space-6px);
  align-items: center;
}

.legend .face {
  width: 18px;
  height: 18px;
}

.footnote {
  margin-inline-start: auto;
  font-weight: var(--fw-medium);
}
